import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown'
import { sortOptionInAlphabeticalOrder } from 'shared/utils'
import { type ActiveFilter, type Option } from 'shared/components/Filter'
import { type EventOption } from 'shared/components/ConversionEventsFlyout'
import { REPORT_TEMPLATE_WITH_CONVERSION_EVENTS, DIMENSIONS } from 'src/home/constants'
import { type Metric, type ReportParameters } from 'src/home/reportTemplateParameters'
import { transformToFilters } from 'src/home/transformations'
import { OPERATOR_OPTIONS, type OperatorWithInputType } from 'shared/constants/filterOperators'
import { getToolFilterValues } from 'shared/copilot/toolRegistryPreviews'
import {
  type HomePageFilterParameter,
  type HomePageTableDimension,
  DETAILED_TABLE_DEFAULT_OPTIONS,
  DETAILED_TABLE_MIN_DIMENSIONS,
  DETAILED_TABLE_MIN_METRICS,
  PROGRESS_OVER_TIME_CHART_MIN_METRICS,
  PROGRESS_OVER_TIME_CHART_MAX_METRICS,
  PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN,
  type ProgressOverTimeChartAggregation,
  type ProgressOverTimeBreakdownOption,
  CONVERSION_EVENT_RELATED_METRICS,
  SCORECARD_METRICS_MAXIMUM_SELECTION,
  SCORECARD_METRICS_MINIMUM_SELECTION,
  type AllowedStandardReport
} from 'src/home/components/utils'

export interface SetPageFilterToolFilter {
  dimensionId: string
  operatorId: string
  /** Preferred: one or more filter values. */
  values?: string[]
  /** Some tool calls send a single value under this key instead of `values`. */
  value?: string
}

export interface SetPageFiltersToolParams {
  filters: SetPageFilterToolFilter[]
}

export interface SelectScorecardMetricsToolParams {
  metrics: string[]
}

export interface SelectConversionEventToolParams {
  eventName: string
  eventSource?: string
}

export interface UpdateProgressOverTimeChartSettingsToolParams {
  metrics: string[]
  breakdown_markets?: string[]
  breakdown_channels?: string[]
  aggregation?: string
}

export interface UpdateDetailedTableSettingsToolParams {
  dimensions: string[]
  metrics: string[]
}

export const HOME_VISUALIZATION_GUIDANCE = {
  detailedTable:
    'Use the detailed data table when the user wants summarized breakdowns for the selected date range, such as total spend per market or conversion value per channel.',
  progressOverTimeChart:
    'Use the progress over time chart when the user wants to see how metrics change over time as a time series, optionally split by market or channel.'
} as const

export type ToolResult = { success: true; message: string } | { success: false; message: string }

/**
 * Reads the `message` field from a copilot tool result object.
 * Returns the fallback when the result is missing or has no message property.
 */
export const getToolResultMessage = (result: unknown, fallback: string): string => {
  if (typeof result === 'object' && result != null && 'message' in result) {
    return String(result.message)
  }

  return fallback
}

/**
 * Reads the `success` flag from a copilot tool result when present.
 * Returns undefined when the result shape does not include a boolean success field.
 */
export const getToolResultSuccess = (result: unknown): boolean | undefined => {
  if (typeof result === 'object' && result != null && 'success' in result) {
    return result.success === true
  }

  return undefined
}

type ProgressOverTimeQueryJsonValue = string | string[] | undefined

/**
 * Parses a JSON-encoded URL query param value used by chart and table settings.
 * Returns undefined when the param is absent, empty, or not valid JSON.
 */
const parseProgressOverTimeQueryJson = <T>(value: ProgressOverTimeQueryJsonValue): T | undefined => {
  if (value == null) return undefined

  const rawValue = Array.isArray(value) ? value[0] : value
  if (rawValue.length === 0) return undefined

  try {
    return JSON.parse(rawValue) as T
  } catch {
    return undefined
  }
}

/**
 * Derives market and channel breakdown dropdown options from loaded report rows.
 * Used by list and update tools to expose valid breakdown ids to the agent.
 */
export const buildProgressOverTimeBreakdownOptionsFromReports = (
  reports: Array<{ market: string; channel: string }>
): {
  marketOptions: ProgressOverTimeBreakdownOption[]
  channelOptions: ProgressOverTimeBreakdownOption[]
} => {
  const { channels, markets } = transformToFilters(reports)

  return {
    marketOptions: Array.from(markets)
      .map((market) => ({
        label: market.name,
        value: market.id
      }))
      .sort(sortOptionInAlphabeticalOrder),
    channelOptions: Array.from(channels)
      .map((channel) => ({
        label: channel.name,
        value: channel.id
      }))
      .sort(sortOptionInAlphabeticalOrder)
  }
}

/**
 * Parses URL query JSON into progress-over-time chart metric, breakdown, and aggregation state.
 * Applies sensible defaults when query params are missing or empty.
 */
export const getProgressOverTimeChartStateFromQuery = (
  query: Record<string, string | string[] | undefined>
): {
  metrics: Metric[]
  marketIds: string[]
  channelIds: string[]
  aggregation: ProgressOverTimeChartAggregation
  comparisonMode: boolean
} => {
  const parsedMetrics = parseProgressOverTimeQueryJson<Metric[]>(query['ts.metrics'])
  const parsedMarkets = parseProgressOverTimeQueryJson<MultiSelectDropdownItem[]>(query['ts.markets']) ?? []
  const parsedChannels = parseProgressOverTimeQueryJson<MultiSelectDropdownItem[]>(query['ts.channels']) ?? []
  const parsedAggregation = parseProgressOverTimeQueryJson<ProgressOverTimeChartAggregation>(query['ts.agg'])
  const comparisonMode = parseProgressOverTimeQueryJson<boolean>(query['ts.comparison']) ?? false

  return {
    metrics: parsedMetrics != null && parsedMetrics.length > 0 ? parsedMetrics : ['conversionValue'],
    marketIds: parsedMarkets.map((market) => market.value),
    channelIds: parsedChannels.map((channel) => channel.value),
    aggregation: parsedAggregation ?? 'daily',
    comparisonMode
  }
}

/**
 * Formats breakdown ids with human-readable labels for tool descriptions.
 * Falls back to the raw id when no matching option exists.
 */
const formatBreakdownIdsForToolDescription = (ids: string[], options: ProgressOverTimeBreakdownOption[]): string => {
  if (ids.length === 0) return 'none'

  return ids
    .map((id) => {
      const option = options.find((entry) => entry.value === id)
      return option == null ? id : `${option.label} (${option.value})`
    })
    .join(', ')
}

/**
 * Summarizes the current progress-over-time chart configuration for tool descriptions.
 * Included in tool handler descriptions so the agent knows the user's current selection.
 */
export const formatCurrentProgressOverTimeChartSettingsForToolDescription = ({
  metrics,
  marketIds,
  channelIds,
  aggregation,
  marketOptions,
  channelOptions,
  getMetricLabel
}: {
  metrics: Metric[]
  marketIds: string[]
  channelIds: string[]
  aggregation: ProgressOverTimeChartAggregation
  marketOptions: ProgressOverTimeBreakdownOption[]
  channelOptions: ProgressOverTimeBreakdownOption[]
  getMetricLabel: (metricId: Metric) => string
}): string => {
  const metricsDescription =
    metrics.length > 0 ? metrics.map((metric) => `${metric} (${getMetricLabel(metric)})`).join(', ') : 'none'

  return `metrics: ${metricsDescription}; markets: ${formatBreakdownIdsForToolDescription(
    marketIds,
    marketOptions
  )}; channels: ${formatBreakdownIdsForToolDescription(channelIds, channelOptions)}; aggregation: ${aggregation}.`
}

/**
 * Builds the list result payload for `list_available_progress_over_time_chart_settings`.
 * Returns selected and available options plus constraints the agent must respect when updating the chart.
 */
export const buildProgressOverTimeChartSettingsList = ({
  currentState,
  availableMetrics,
  marketOptions,
  channelOptions,
  getMetricLabel,
  isDataLoaded
}: {
  currentState: ProgressOverTimeChartCurrentState
  availableMetrics: Metric[]
  marketOptions: ProgressOverTimeBreakdownOption[]
  channelOptions: ProgressOverTimeBreakdownOption[]
  getMetricLabel: (metricId: Metric) => string
  isDataLoaded: boolean
}): {
  isDataLoaded: boolean
  visualizationGuidance: typeof HOME_VISUALIZATION_GUIDANCE
  constraints: {
    minimumSelectedMetrics: number
    maximumSelectedMetrics: number
    maximumSelectedMetricsWithBreakdown: number
    maximumBreakdownSelections: number
    rules: string[]
  }
  selectedSettings: {
    metrics: Array<{ id: Metric; label: string }>
    markets: Array<{ id: string; label: string }>
    channels: Array<{ id: string; label: string }>
    aggregation: ProgressOverTimeChartAggregation
  }
  availableMetrics: Array<{ id: Metric; label: string }>
  availableMarkets: ProgressOverTimeBreakdownOption[]
  availableChannels: ProgressOverTimeBreakdownOption[]
  availableAggregations: Array<{ id: ProgressOverTimeChartAggregation; description: string }>
  message: string
} => {
  const { metrics, marketIds, channelIds, aggregation } = currentState

  const selectedMarkets = marketIds.map((id) => {
    const option = marketOptions.find((entry) => entry.value === id)
    return { id, label: option?.label ?? id }
  })
  const selectedChannels = channelIds.map((id) => {
    const option = channelOptions.find((entry) => entry.value === id)
    return { id, label: option?.label ?? id }
  })

  return {
    isDataLoaded,
    visualizationGuidance: HOME_VISUALIZATION_GUIDANCE,
    constraints: {
      minimumSelectedMetrics: PROGRESS_OVER_TIME_CHART_MIN_METRICS,
      maximumSelectedMetrics: PROGRESS_OVER_TIME_CHART_MAX_METRICS,
      maximumSelectedMetricsWithBreakdown: PROGRESS_OVER_TIME_CHART_MIN_METRICS,
      maximumBreakdownSelections: PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN,
      rules: [
        'Select up to 2 metrics when no breakdown is selected.',
        'Select exactly 1 metric when any market or channel breakdown is selected.',
        'Select either markets or channels for breakdown, never both at the same time.',
        'Select up to 6 breakdown values of the same type.',
        'Pass an empty breakdown array to clear markets or channels.',
        'Default aggregation is daily unless the user asks for cumulative.'
      ]
    },
    selectedSettings: {
      metrics: metrics.map((metric) => ({ id: metric, label: getMetricLabel(metric) })),
      markets: selectedMarkets,
      channels: selectedChannels,
      aggregation
    },
    availableMetrics: availableMetrics.map((metric) => ({
      id: metric,
      label: getMetricLabel(metric)
    })),
    availableMarkets: marketOptions,
    availableChannels: channelOptions,
    availableAggregations: [
      {
        id: 'daily',
        description: 'Shows daily values for the selected metrics and breakdowns.'
      },
      {
        id: 'cumulative',
        description: 'Shows cumulative values for the selected metrics and breakdowns.'
      }
    ],
    message: isDataLoaded
      ? 'Progress over time chart settings are available.'
      : 'Data is not loaded yet. Breakdown options may be empty until data is available.'
  }
}

/**
 * Maps breakdown ids to multiselect items, validating each id against available options.
 * Returns a tool error result when any id does not match a known market or channel.
 */
const buildProgressOverTimeBreakdownItems = ({
  ids,
  options,
  subCategory
}: {
  ids: string[]
  options: ProgressOverTimeBreakdownOption[]
  subCategory: string
}): { items: MultiSelectDropdownItem[] } | ToolResult => {
  const items: MultiSelectDropdownItem[] = []

  for (const id of ids) {
    const option = options.find((entry) => entry.value === id || entry.label === id)
    if (option == null) {
      return {
        success: false,
        message: `Unknown breakdown value "${id}". Call list_available_progress_over_time_chart_settings to inspect available market and channel ids.`
      }
    }

    items.push({
      label: option.label,
      value: option.value,
      subCategory
    })
  }

  return { items }
}

/**
 * Compares two string arrays for equal length and matching values at each index.
 * Used to skip no-op updates when tool params match the current UI state.
 */
const areSameStringArrays = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false

  return left.every((value, index) => value === right[index])
}

export interface ProgressOverTimeChartCurrentState {
  metrics: Metric[]
  marketIds: string[]
  channelIds: string[]
  aggregation: ProgressOverTimeChartAggregation
}

export interface ApplyProgressOverTimeChartSettingsSetters {
  setMetrics: (value: Metric[]) => void
  setChannels: (value: MultiSelectDropdownItem[]) => void
  setMarkets: (value: MultiSelectDropdownItem[]) => void
  setAggregation: (value: ProgressOverTimeChartAggregation) => void
}

/**
 * Validates and applies progress-over-time chart settings from a copilot tool call.
 * Enforces metric, breakdown, and aggregation rules before mutating chart query state.
 */
export const applyProgressOverTimeChartSettings = ({
  params,
  currentState,
  availableMetrics,
  marketOptions,
  channelOptions,
  marketsSubCategoryLabel,
  channelsSubCategoryLabel,
  setChartState
}: {
  params: UpdateProgressOverTimeChartSettingsToolParams
  currentState: ProgressOverTimeChartCurrentState
  availableMetrics: Metric[]
  marketOptions: ProgressOverTimeBreakdownOption[]
  channelOptions: ProgressOverTimeBreakdownOption[]
  marketsSubCategoryLabel: string
  channelsSubCategoryLabel: string
  setChartState: ApplyProgressOverTimeChartSettingsSetters
}): ToolResult => {
  const requestedMetrics = params.metrics
  const uniqueRequestedMetrics = new Set(requestedMetrics)

  if (uniqueRequestedMetrics.size !== requestedMetrics.length) {
    return {
      success: false,
      message: 'Progress over time chart metrics cannot contain duplicate metric ids.'
    }
  }

  if (requestedMetrics.length < PROGRESS_OVER_TIME_CHART_MIN_METRICS) {
    return {
      success: false,
      message: `Select at least ${PROGRESS_OVER_TIME_CHART_MIN_METRICS} metric for the progress over time chart.`
    }
  }

  if (requestedMetrics.length > PROGRESS_OVER_TIME_CHART_MAX_METRICS) {
    return {
      success: false,
      message: `Select at most ${PROGRESS_OVER_TIME_CHART_MAX_METRICS} metrics for the progress over time chart.`
    }
  }

  const availableMetricIds = new Set(availableMetrics)
  const invalidMetric = requestedMetrics.find((metric) => !availableMetricIds.has(metric as Metric))
  if (invalidMetric != null) {
    return {
      success: false,
      message: `Unknown progress over time metric "${invalidMetric}". Call list_available_progress_over_time_chart_settings to inspect available metrics.`
    }
  }

  const metrics = requestedMetrics as Metric[]
  const hasMarketsParam = params.breakdown_markets != null
  const hasChannelsParam = params.breakdown_channels != null
  let requestedMarketIds: string[]
  let requestedChannelIds: string[]

  if (hasMarketsParam && hasChannelsParam) {
    requestedMarketIds = params.breakdown_markets ?? []
    requestedChannelIds = params.breakdown_channels ?? []
  } else if (hasMarketsParam) {
    requestedMarketIds = params.breakdown_markets ?? []
    requestedChannelIds = []
  } else if (hasChannelsParam) {
    requestedChannelIds = params.breakdown_channels ?? []
    requestedMarketIds = []
  } else {
    requestedMarketIds = currentState.marketIds
    requestedChannelIds = currentState.channelIds
  }

  if (hasMarketsParam && hasChannelsParam && requestedMarketIds.length > 0 && requestedChannelIds.length > 0) {
    return {
      success: false,
      message: 'Select either markets or channels for the breakdown, not both at the same time.'
    }
  }

  if (!hasMarketsParam && !hasChannelsParam && requestedMarketIds.length > 0 && requestedChannelIds.length > 0) {
    return {
      success: false,
      message: 'The progress over time chart cannot keep both market and channel breakdowns selected at the same time.'
    }
  }

  if (requestedMarketIds.length > PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN) {
    return {
      success: false,
      message: `Select at most ${PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN} market breakdowns.`
    }
  }

  if (requestedChannelIds.length > PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN) {
    return {
      success: false,
      message: `Select at most ${PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN} channel breakdowns.`
    }
  }

  const hasBreakdown = requestedMarketIds.length > 0 || requestedChannelIds.length > 0
  if (hasBreakdown && metrics.length > PROGRESS_OVER_TIME_CHART_MIN_METRICS) {
    return {
      success: false,
      message: 'When a market or channel breakdown is selected, choose exactly one metric.'
    }
  }

  const aggregation = params.aggregation ?? currentState.aggregation
  if (aggregation !== 'daily' && aggregation !== 'cumulative') {
    return {
      success: false,
      message: 'Aggregation must be either "daily" or "cumulative".'
    }
  }

  let markets: MultiSelectDropdownItem[] = []
  if (requestedMarketIds.length > 0) {
    const marketItemsResult = buildProgressOverTimeBreakdownItems({
      ids: requestedMarketIds,
      options: marketOptions,
      subCategory: marketsSubCategoryLabel
    })
    if (!('items' in marketItemsResult)) return marketItemsResult
    markets = marketItemsResult.items
  }

  let channels: MultiSelectDropdownItem[] = []
  if (requestedChannelIds.length > 0) {
    const channelItemsResult = buildProgressOverTimeBreakdownItems({
      ids: requestedChannelIds,
      options: channelOptions,
      subCategory: channelsSubCategoryLabel
    })
    if (!('items' in channelItemsResult)) return channelItemsResult
    channels = channelItemsResult.items
  }

  if (
    areSameStringArrays(metrics, currentState.metrics) &&
    areSameStringArrays(requestedMarketIds, currentState.marketIds) &&
    areSameStringArrays(requestedChannelIds, currentState.channelIds) &&
    aggregation === currentState.aggregation
  ) {
    return {
      success: true,
      message: 'Progress over time chart settings already match the requested configuration.'
    }
  }

  setChartState.setMetrics(metrics)
  setChartState.setChannels(channels)
  setChartState.setMarkets(markets)
  setChartState.setAggregation(aggregation)

  return {
    success: true,
    message: 'Updated progress over time chart settings.'
  }
}

/**
 * Type guard for detailed table dimension ids defined on the home page.
 * Ensures only known grouping dimensions are accepted from tool params.
 */
const isHomePageTableDimension = (value: string): value is HomePageTableDimension => {
  return (DIMENSIONS as readonly string[]).includes(value)
}

/**
 * Parses URL query JSON into detailed table dimension and metric selections.
 * Falls back to default columns when the table options query param is absent.
 */
export const getDetailedTableStateFromQuery = (
  query: Record<string, string | string[] | undefined>,
  availableMetrics: Metric[]
): {
  dimensions: HomePageTableDimension[]
  metrics: Metric[]
} => {
  const parsedOptions = parseProgressOverTimeQueryJson<string[]>(query['table.options'])
  const selectedOptions =
    parsedOptions != null && parsedOptions.length > 0 ? parsedOptions : [...DETAILED_TABLE_DEFAULT_OPTIONS]
  const availableMetricIds = new Set(availableMetrics)

  return {
    dimensions: selectedOptions.filter(isHomePageTableDimension),
    metrics: selectedOptions.filter((option): option is Metric => availableMetricIds.has(option as Metric))
  }
}

/**
 * Summarizes the current detailed table column configuration for tool descriptions.
 * Included in tool handler descriptions so the agent knows the user's current columns.
 */
export const formatCurrentDetailedTableSettingsForToolDescription = ({
  dimensions,
  metrics,
  getDimensionLabel,
  getMetricLabel
}: {
  dimensions: HomePageTableDimension[]
  metrics: Metric[]
  getDimensionLabel: (dimensionId: HomePageTableDimension) => string
  getMetricLabel: (metricId: Metric) => string
}): string => {
  const dimensionsDescription =
    dimensions.length > 0
      ? dimensions.map((dimension) => `${dimension} (${getDimensionLabel(dimension)})`).join(', ')
      : 'none'
  const metricsDescription =
    metrics.length > 0 ? metrics.map((metric) => `${metric} (${getMetricLabel(metric)})`).join(', ') : 'none'

  return `dimensions: ${dimensionsDescription}; metrics: ${metricsDescription}.`
}

/**
 * Builds the list result payload for `list_available_detailed_table_settings`.
 * Returns selected and available dimensions and metrics plus layout constraints.
 */
export const buildDetailedTableSettingsList = ({
  currentState,
  availableMetrics,
  getDimensionLabel,
  getMetricLabel,
  isDataLoaded
}: {
  currentState: DetailedTableCurrentState
  availableMetrics: Metric[]
  getDimensionLabel: (dimensionId: HomePageTableDimension) => string
  getMetricLabel: (metricId: Metric) => string
  isDataLoaded: boolean
}): {
  isDataLoaded: boolean
  visualizationGuidance: typeof HOME_VISUALIZATION_GUIDANCE
  constraints: {
    minimumDimensions: number
    minimumMetrics: number
    rules: string[]
  }
  selectedSettings: {
    dimensions: Array<{ id: HomePageTableDimension; label: string }>
    metrics: Array<{ id: Metric; label: string }>
  }
  availableDimensions: Array<{ id: HomePageTableDimension; label: string }>
  availableMetrics: Array<{ id: Metric; label: string }>
  message: string
} => {
  const { dimensions, metrics } = currentState

  return {
    isDataLoaded,
    visualizationGuidance: HOME_VISUALIZATION_GUIDANCE,
    constraints: {
      minimumDimensions: DETAILED_TABLE_MIN_DIMENSIONS,
      minimumMetrics: DETAILED_TABLE_MIN_METRICS,
      rules: [
        'Select at least one dimension and one metric.',
        'Dimensions define how rows are grouped, such as by channel, market, or date.',
        'Metrics define which performance values appear as columns.',
        'Provide the full desired dimension and metric selections when updating the table.'
      ]
    },
    selectedSettings: {
      dimensions: dimensions.map((dimension) => ({
        id: dimension,
        label: getDimensionLabel(dimension)
      })),
      metrics: metrics.map((metric) => ({
        id: metric,
        label: getMetricLabel(metric)
      }))
    },
    availableDimensions: DIMENSIONS.map((dimension) => ({
      id: dimension,
      label: getDimensionLabel(dimension)
    })),
    availableMetrics: availableMetrics.map((metric) => ({
      id: metric,
      label: getMetricLabel(metric)
    })),
    message: isDataLoaded
      ? 'Detailed table column settings are available.'
      : 'Data is not loaded yet. Dimension and metric ids are still available, but the table may be empty until data loads.'
  }
}

export interface DetailedTableCurrentState {
  dimensions: HomePageTableDimension[]
  metrics: Metric[]
}

/**
 * Validates and applies detailed table dimension and metric selections from a copilot tool call.
 * Requires at least one dimension and one metric before updating table query state.
 */
export const applyDetailedTableSettings = ({
  params,
  currentState,
  availableMetrics,
  setSelectedOptions
}: {
  params: UpdateDetailedTableSettingsToolParams
  currentState: DetailedTableCurrentState
  availableMetrics: Metric[]
  setSelectedOptions: (value: string[]) => void
}): ToolResult => {
  const requestedDimensions = params.dimensions
  const requestedMetrics = params.metrics
  const uniqueRequestedDimensions = new Set(requestedDimensions)
  const uniqueRequestedMetrics = new Set(requestedMetrics)

  if (uniqueRequestedDimensions.size !== requestedDimensions.length) {
    return {
      success: false,
      message: 'Detailed table dimensions cannot contain duplicate ids.'
    }
  }

  if (uniqueRequestedMetrics.size !== requestedMetrics.length) {
    return {
      success: false,
      message: 'Detailed table metrics cannot contain duplicate ids.'
    }
  }

  if (requestedDimensions.length < DETAILED_TABLE_MIN_DIMENSIONS) {
    return {
      success: false,
      message: `Select at least ${DETAILED_TABLE_MIN_DIMENSIONS} dimension for the detailed table.`
    }
  }

  if (requestedMetrics.length < DETAILED_TABLE_MIN_METRICS) {
    return {
      success: false,
      message: `Select at least ${DETAILED_TABLE_MIN_METRICS} metric for the detailed table.`
    }
  }

  const invalidDimension = requestedDimensions.find((dimension) => !isHomePageTableDimension(dimension))
  if (invalidDimension != null) {
    return {
      success: false,
      message: `Unknown detailed table dimension "${invalidDimension}". Call list_available_detailed_table_settings to inspect available dimensions.`
    }
  }

  const availableMetricIds = new Set(availableMetrics)
  const invalidMetric = requestedMetrics.find((metric) => !availableMetricIds.has(metric as Metric))
  if (invalidMetric != null) {
    return {
      success: false,
      message: `Unknown detailed table metric "${invalidMetric}". Call list_available_detailed_table_settings to inspect available metrics.`
    }
  }

  const dimensions = requestedDimensions as HomePageTableDimension[]
  const metrics = requestedMetrics as Metric[]

  if (areSameStringArrays(dimensions, currentState.dimensions) && areSameStringArrays(metrics, currentState.metrics)) {
    return {
      success: true,
      message: 'Detailed table settings already match the requested configuration.'
    }
  }

  setSelectedOptions([...dimensions, ...metrics])

  return {
    success: true,
    message: `Updated detailed table to show ${dimensions.length} dimension${dimensions.length === 1 ? '' : 's'} and ${
      metrics.length
    } metric${metrics.length === 1 ? '' : 's'}.`
  }
}

/**
 * Resolves the operator map for a filter dimension, falling back to global defaults.
 * Dimension-specific operators override the shared operator catalog when configured.
 */
const getFilterOperators = (parameterOption: Option): Record<string, OperatorWithInputType> => {
  return parameterOption.operators ?? OPERATOR_OPTIONS
}

/**
 * Serializes filter dimensions, operators, values, and current selections for copilot list tools.
 * Gives the agent everything needed to construct valid `set_page_filters` calls.
 */
export const serializeFilterOptions = (
  parameterOptions: Option[],
  selectedFilters: Array<ActiveFilter<HomePageFilterParameter>>,
  canFilter: boolean,
  isFilterOptionsLoaded: boolean
): object => {
  return {
    canFilter,
    isFilterOptionsLoaded,
    selectedFilters: selectedFilters.map((filter) => ({
      dimensionId: filter.parameter.value,
      dimensionLabel: filter.parameter.label,
      operatorId: filter.operator,
      values:
        filter.data == null
          ? []
          : Array.isArray(filter.data)
          ? filter.data.map((value) => value.value)
          : [typeof filter.data === 'string' ? filter.data : filter.data.value]
    })),
    dimensions: parameterOptions.map((parameterOption) => ({
      id: parameterOption.value,
      label: parameterOption.label,
      operators: Object.values(getFilterOperators(parameterOption)).map((operator) => ({
        id: operator.id,
        label: operator.label,
        inputType: operator.type
      })),
      values:
        parameterOption.options?.map((option) => ({
          id: option.value,
          label: option.label
        })) ?? []
    }))
  }
}

/**
 * Formats active filter value(s) as a display label for tool descriptions.
 * Valueless operators such as is null and is not null have no data.
 */
const getFilterDataLabel = (data: ActiveFilter<HomePageFilterParameter>['data']): string => {
  if (data == null) {
    return ''
  }

  if (Array.isArray(data)) {
    return data.map((value) => value.label ?? value.value).join(', ')
  }

  if (typeof data === 'string') {
    return data
  }

  return data.label
}

/**
 * Formats active homepage filters as a semicolon-separated string for tool descriptions.
 * Returns `none` when no filters are applied.
 */
export const formatSelectedFiltersForToolDescription = (
  selectedFilters: Array<ActiveFilter<HomePageFilterParameter>>
): string => {
  if (selectedFilters.length === 0) return 'none'

  return selectedFilters
    .map((filter) => {
      if (filter.data == null) {
        return `${filter.parameter.value} ${filter.operator}`
      }

      return `${filter.parameter.value} ${filter.operator} ${getFilterDataLabel(filter.data)}`
    })
    .join('; ')
}

/**
 * Formats the selected conversion event (with source) for tool descriptions.
 * Returns `none` when no conversion event is selected.
 */
export const formatSelectedConversionEventForToolDescription = (
  selectedConversionEvents: MultiSelectDropdownItem[] | undefined
): string => {
  if (selectedConversionEvents?.[0] == null) return 'none'

  const event = selectedConversionEvents[0]
  const sourceSuffix = event.subCategory == null ? '' : ` from ${event.subCategory}`

  return `${event.value}${sourceSuffix}`
}

/**
 * Finds a nested filter option by value or label within a dimension.
 * Used when resolving tool-provided filter values to UI option objects.
 */
const findFilterOptionValue = (parameterOption: Option, value: string): Option | undefined => {
  return parameterOption.options?.find((option) => option.value === value || option.label === value)
}

/**
 * Converts copilot filter tool params into validated `ActiveFilter` objects for the homepage.
 * Returns a structured error when dimension, operator, or value combinations are invalid.
 */
export const createActiveFiltersFromToolFilters = (
  filters: SetPageFilterToolFilter[],
  parameterOptions: Option[]
): { success: true; filters: Array<ActiveFilter<HomePageFilterParameter>> } | ToolResult => {
  const activeFilters: Array<ActiveFilter<HomePageFilterParameter>> = []

  for (const filter of filters) {
    const parameterOption = parameterOptions.find((option) => option.value === filter.dimensionId)
    if (parameterOption == null) {
      return {
        success: false,
        message: `Unknown filter dimension "${filter.dimensionId}". Call list_filter_options to inspect available dimensions.`
      }
    }

    const operator = getFilterOperators(parameterOption)[filter.operatorId]
    if (operator == null) {
      return {
        success: false,
        message: `Unknown operator "${filter.operatorId}" for dimension "${parameterOption.label}". Call list_filter_options to inspect available operators.`
      }
    }

    const values = getToolFilterValues(filter).filter((value) => value.trim().length > 0)

    if (operator.type === null) {
      activeFilters.push({
        parameter: parameterOption as Option<HomePageFilterParameter>,
        operator: operator.id,
        data: null
      })
      continue
    }

    if (values.length === 0) {
      return {
        success: false,
        message: `Filter "${parameterOption.label}" needs at least one value.`
      }
    }

    if (operator.type === 'multi_select') {
      const selectedOptions = values.map((value) => findFilterOptionValue(parameterOption, value))
      const missingValue = values.find((_, index) => selectedOptions[index] == null)
      if (missingValue != null) {
        return {
          success: false,
          message: `Unknown value "${missingValue}" for dimension "${parameterOption.label}". Call list_filter_options to inspect available values.`
        }
      }

      activeFilters.push({
        parameter: parameterOption as Option<HomePageFilterParameter>,
        operator: operator.id,
        data: selectedOptions as Option[]
      })
      continue
    }

    if (values.length > 1) {
      return {
        success: false,
        message: `Operator "${operator.label}" for dimension "${parameterOption.label}" accepts exactly one value.`
      }
    }

    if (operator.type === 'single_select') {
      const selectedOption = findFilterOptionValue(parameterOption, values[0])
      if (selectedOption == null) {
        return {
          success: false,
          message: `Unknown value "${values[0]}" for dimension "${parameterOption.label}". Call list_filter_options to inspect available values.`
        }
      }

      activeFilters.push({
        parameter: parameterOption as Option<HomePageFilterParameter>,
        operator: operator.id,
        data: selectedOption
      })
      continue
    }

    activeFilters.push({
      parameter: parameterOption as Option<HomePageFilterParameter>,
      operator: operator.id,
      data: values[0]
    })
  }

  return { success: true, filters: activeFilters }
}

/**
 * Finds a conversion event option matching name and optional source from loaded event options.
 * Matches by value or label; source narrows ambiguous event names.
 */
export const findConversionEventOption = (
  eventOptions: EventOption[] | undefined,
  eventName: string,
  eventSource?: string
): EventOption | undefined => {
  return eventOptions?.find((eventOption) => {
    const hasMatchingName = eventOption.value === eventName || eventOption.label === eventName
    const hasMatchingSource = eventSource == null || eventOption.subCategory === eventSource
    return hasMatchingName && hasMatchingSource
  })
}

/**
 * Returns whether the given event option is already the sole selected conversion event.
 * Used to short-circuit no-op `select_conversion_event` tool calls.
 */
export const isSameConversionEvent = (
  selectedConversionEvents: MultiSelectDropdownItem[] | undefined,
  eventOption: EventOption
): boolean => {
  return (
    selectedConversionEvents?.length === 1 &&
    selectedConversionEvents[0].value === eventOption.value &&
    selectedConversionEvents[0].subCategory === eventOption.subCategory
  )
}

/**
 * Formats an optional conversion event source suffix for user-facing tool messages.
 * Appends ` from {source}` when the event has a subCategory.
 */
const formatConversionEventSourceSuffix = (subCategory: string | undefined): string =>
  subCategory == null ? '' : ` from ${subCategory}`

/**
 * Builds the list result payload for `list_available_conversion_events`.
 * Includes the current selection, affected scorecard metrics, and available events with counts.
 */
export const buildListAvailableConversionEventsResult = ({
  isLoadingConversionNamesAndSources,
  eventOptions,
  selectedConversionEvents,
  selectedTopCardMetrics,
  getMetricLabel
}: {
  isLoadingConversionNamesAndSources: boolean
  eventOptions: EventOption[] | undefined
  selectedConversionEvents: MultiSelectDropdownItem[] | undefined
  selectedTopCardMetrics: Metric[]
  getMetricLabel: (metric: Metric) => string
}): {
  reportSupportsConversionEvents: true
  isLoading: boolean
  isLoaded: boolean
  selectedConversionEvent: Array<{
    eventName: string
    eventLabel: string
    eventSource: string | undefined
  }>
  affectedMetrics: Array<{
    id: Metric
    label: string
    currentlyVisibleInScorecardGroup: boolean
  }>
  availableConversionEvents: Array<{
    eventName: string
    eventLabel: string
    eventSource: string | undefined
    numberOfConversionsInDateRange: number | null | undefined
  }>
  message: string
} => ({
  reportSupportsConversionEvents: true,
  isLoading: isLoadingConversionNamesAndSources,
  isLoaded: eventOptions != null,
  selectedConversionEvent:
    selectedConversionEvents?.map((event) => ({
      eventName: event.value,
      eventLabel: event.label,
      eventSource: event.subCategory == null || event.subCategory === '' ? undefined : event.subCategory
    })) ?? [],
  affectedMetrics: CONVERSION_EVENT_RELATED_METRICS.map((metric) => ({
    id: metric,
    label: getMetricLabel(metric),
    currentlyVisibleInScorecardGroup: selectedTopCardMetrics.includes(metric)
  })),
  availableConversionEvents:
    eventOptions?.map((event) => ({
      eventName: event.value,
      eventLabel: event.label,
      eventSource: event.subCategory == null || event.subCategory === '' ? undefined : event.subCategory,
      numberOfConversionsInDateRange: event.numberOfConversions
    })) ?? [],
  message:
    eventOptions == null
      ? 'Conversion event options are loading. Call this tool again before selecting an event.'
      : 'Conversion event options are available.'
})

/**
 * Handles the `select_conversion_event` tool: validates, persists, and applies the selection.
 * Loads event options on demand and rejects ambiguous names without an explicit source.
 */
export const handleSelectConversionEventTool = ({
  params,
  selectedStandardReportConfig,
  reportParameters,
  eventOptions,
  selectedConversionEvents,
  setSelectedConversionEvents,
  loadConversionEventOptions
}: {
  params: SelectConversionEventToolParams
  selectedStandardReportConfig: AllowedStandardReport | undefined
  reportParameters: ReportParameters | undefined
  eventOptions: EventOption[] | undefined
  selectedConversionEvents: MultiSelectDropdownItem[] | undefined
  setSelectedConversionEvents: (events: MultiSelectDropdownItem[]) => void
  loadConversionEventOptions: () => void
}): ToolResult => {
  if (
    selectedStandardReportConfig == null ||
    reportParameters == null ||
    !REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId)
  ) {
    return {
      success: false,
      message: 'The currently selected report does not support choosing a conversion event goal.'
    }
  }

  if (eventOptions == null) {
    loadConversionEventOptions()
    return {
      success: false,
      message: 'Conversion event options are still loading. Call list_available_conversion_events before selecting.'
    }
  }

  const matchingEvents = eventOptions.filter(
    (event) => event.value === params.eventName || event.label === params.eventName
  )
  if (params.eventSource == null && matchingEvents.length > 1) {
    return {
      success: false,
      message: `Multiple conversion events are named "${params.eventName}". Call list_available_conversion_events and provide eventSource.`
    }
  }

  const eventOption = findConversionEventOption(eventOptions, params.eventName, params.eventSource)
  if (eventOption == null) {
    return {
      success: false,
      message: `Could not find conversion event "${params.eventName}". Call list_available_conversion_events to inspect available events.`
    }
  }

  if (isSameConversionEvent(selectedConversionEvents, eventOption)) {
    return {
      success: true,
      message: `Conversion event "${eventOption.label}"${formatConversionEventSourceSuffix(
        eventOption.subCategory
      )} is already selected.`
    }
  }

  const selectedEvent = [eventOption]
  setSelectedConversionEvents(selectedEvent)
  reportParameters.storeSelectedConversionEvents?.(selectedStandardReportConfig.configId.toString(), selectedEvent)

  return {
    success: true,
    message: `Selected conversion event "${eventOption.label}"${formatConversionEventSourceSuffix(
      eventOption.subCategory
    )}.`
  }
}

/**
 * Handles the `select_metrics_in_scorecard_group` tool: validates and updates visible scorecard metrics.
 * Enforces min/max selection counts and persists the choice to report storage.
 */
export const handleSelectScorecardMetricsTool = ({
  params,
  reportParameters,
  listOfMetricsToShowInDropDownWithBudgetMetrics,
  setSelectedTopCardMetrics,
  saveMetricsToStore
}: {
  params: SelectScorecardMetricsToolParams
  reportParameters: ReportParameters | undefined
  listOfMetricsToShowInDropDownWithBudgetMetrics: Metric[]
  setSelectedTopCardMetrics: (metrics: Metric[]) => void
  saveMetricsToStore: (metrics: Metric[]) => void
}): ToolResult => {
  if (reportParameters == null) {
    return {
      success: false,
      message: 'No report is currently selected, so scorecard metrics cannot be changed.'
    }
  }

  const requestedMetrics = params.metrics
  const uniqueRequestedMetrics = new Set(requestedMetrics)
  if (uniqueRequestedMetrics.size !== requestedMetrics.length) {
    return {
      success: false,
      message: 'Scorecard metric selection cannot contain duplicate metric ids.'
    }
  }

  if (requestedMetrics.length < SCORECARD_METRICS_MINIMUM_SELECTION) {
    return {
      success: false,
      message: `Select at least ${SCORECARD_METRICS_MINIMUM_SELECTION} scorecard metrics.`
    }
  }

  if (requestedMetrics.length > SCORECARD_METRICS_MAXIMUM_SELECTION) {
    return {
      success: false,
      message: `Select at most ${SCORECARD_METRICS_MAXIMUM_SELECTION} scorecard metrics.`
    }
  }

  const availableMetricIds = new Set(listOfMetricsToShowInDropDownWithBudgetMetrics)
  const invalidMetric = requestedMetrics.find((metric) => !availableMetricIds.has(metric as Metric))
  if (invalidMetric != null) {
    return {
      success: false,
      message: `Unknown scorecard metric "${invalidMetric}". Call list_available_metrics_for_scorecard_group to inspect available metrics.`
    }
  }

  const metrics = requestedMetrics as Metric[]
  setSelectedTopCardMetrics(metrics)
  saveMetricsToStore(metrics)

  return {
    success: true,
    message: `Updated scorecard group to show ${metrics.length} metric${metrics.length === 1 ? '' : 's'}.`
  }
}
